import type { JSX } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { cx } from './cx';

/**
 * The icon-and-sentence row: a leading glyph, then one paragraph explaining
 * something the screen cannot show as a number.
 *
 * It covers Home's `ProgressNotice` — the one-line explanation under the
 * progress bar for a run that is not advancing — and the Settings rows that
 * **replace** Android's battery-optimisation button, its OEM autostart button
 * and its whole Permissions module. None of those exist inside Safari and none
 * of them can be read, requested or opened from a WebClip, so the honest
 * substitution there is an instruction to the operator rather than a control
 * that would do nothing.
 *
 * Those two screens had built the same three-element structure four times over
 * between them, which is three chances for one of them to drift out of the
 * `.note-row` / `.note-row__icon` / `.note-row__text` contract `components.css`
 * is written against. This is that structure, once.
 */
export function NoteRow({
  icon,
  text,
  tint,
  size,
  className,
  textClassName = 't-body-medium',
}: {
  icon: IconName;
  text: string;
  /**
   * A `.c-*` colour utility for the **icon**, never an inline colour.
   *
   * A class rather than a `var(--…)` string because that keeps the set of
   * colours this row can take enumerable — grep-able — instead of being any
   * string a caller invents, which is the convention `components.css` opens
   * with. `.note-row__icon` reads `var(--value-color, var(--error))`, so an
   * untinted row is still the error colour: most notes are something being
   * wrong.
   *
   * `c-primary-graphic` is legal here and only here-shaped: --primary is
   * 3.84:1 on the page, which clears the 3:1 WCAG 1.4.11 asks of a non-text
   * element and misses the 4.5:1 a word needs — so it may tint the glyph while
   * the sentence beside it stays --text-secondary.
   */
  tint?: NoteTint;
  /** Icon size in px. Defaults to {@link Icon}'s own 20 — Android's `Spacing.iconRow`. */
  size?: number;
  /** Extra classes for the row itself, e.g. Home's `progress-notice` top margin. */
  className?: string;
  /** Typography (and any layout) class for the paragraph. */
  textClassName?: string;
}): JSX.Element {
  return (
    <div className={cx('note-row', className)}>
      <Icon name={icon} size={size} className={cx('note-row__icon', tint)} />
      <p className={cx('note-row__text', textClassName)}>{text}</p>
    </div>
  );
}

/**
 * The colours a note row's icon is allowed to be.
 *
 * Every member is a utility defined at the top of `components.css`; adding one
 * that is not already a token is what the comment there forbids.
 */
export type NoteTint =
  | 'c-alive'
  | 'c-dead'
  | 'c-warn'
  | 'c-error'
  | 'c-accent'
  | 'c-primary-graphic';
